import { useEffect, useRef, useState } from "react";

/**
 * Touch/viewport trigger for the homepage service-icon micro-animations
 * (Sep 2026). Touch devices have no reliable hover — and the first tap on
 * a card must stay a pure navigation — so on coarse-pointer devices each
 * icon's animation plays ONCE when ~35% of its card scrolls into view.
 * Cards entering in the same frame stagger at 100ms (almost subconscious,
 * never a cascade). Desktop hover is pure CSS and never touches this hook.
 * Reduced-motion visitors are skipped entirely (CSS also gates them).
 *
 * Usage: const { played, register } = usePlayOnceInView(depKey);
 *   - register(key) → ref callback for the CARD element
 *   - played[key]   → stagger delay in ms once triggered (undefined = not yet)
 */
export const usePlayOnceInView = (depKey) => {
  const [played, setPlayed] = useState({});
  const nodes = useRef(new Map());

  useEffect(() => {
    if (typeof window === "undefined") return;
    const coarse = window.matchMedia("(hover: none), (pointer: coarse)").matches;
    const reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (!coarse || reduced || typeof IntersectionObserver === "undefined") return;

    const io = new IntersectionObserver(
      (entries) => {
        const batch = entries.filter((e) => e.isIntersecting);
        if (!batch.length) return;
        // Visual reading order so the stagger follows the layout.
        batch.sort(
          (a, b) =>
            a.boundingClientRect.top - b.boundingClientRect.top ||
            a.boundingClientRect.left - b.boundingClientRect.left
        );
        setPlayed((prev) => {
          let changed = false;
          const next = { ...prev };
          batch.forEach((e, i) => {
            const k = e.target.getAttribute("data-icon-key");
            if (k && !(k in next)) {
              next[k] = i * 100;
              changed = true;
            }
            io.unobserve(e.target); // once per page load
          });
          return changed ? next : prev;
        });
      },
      { threshold: 0.35 }
    );
    nodes.current.forEach((el) => io.observe(el));
    return () => io.disconnect();
  }, [depKey]);

  const register = (key) => (el) => {
    if (el) {
      el.setAttribute("data-icon-key", key);
      nodes.current.set(key, el);
    } else {
      nodes.current.delete(key);
    }
  };

  return { played, register };
};
